import { APIPath, Entry } from '@/cms';
import { getStrapiApiPathForSlug, sanitiseSlug } from '@/cms/utils';
import { selfEndpoint } from '@/constant/env';
import { env } from '@/constant/process';
import { Region } from '@/features/Header/constants/regions';
import { currentLocaleConfig } from '@/lib/i18n/localeSpecific';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

type Props = { region: Region };

async function getOtherLocaleMatchingUrl(
  pathname: string,
  targetLocale: Region['locale']
) {
  const currentSlug = sanitiseSlug(pathname);
  const currentPageApi = getStrapiApiPathForSlug(pathname) as APIPath;

  let normalizedSlug = currentSlug;

  if (currentPageApi === '/blogs') {
    normalizedSlug = currentSlug.replace('/blog', '');
  }

  const res = await getAllValidPageAndBlogPaths();
  if (!res) return null;

  const data = res[currentPageApi];

  const otherLocaleMatchingUrl = data?.find((item) => {
    if (item.slug !== normalizedSlug) return false;
    if (item.locale?.toLowerCase() !== targetLocale.toLowerCase()) return false;

    return true;
  });

  return otherLocaleMatchingUrl || null;
}

async function getAllValidPageAndBlogPaths(): Promise<Record<
  APIPath,
  Entry[]
> | null> {
  const response = await fetch(`/next-api/paths`);

  if (!response.ok) return null;
  const data = await response.json();

  return data as Record<APIPath, Entry[]>;
}

const createRedirectLinkForLocale = (pathname: string, region: Region) => {
  const { locale: targetLocale } = region;

  let targetHost: string = '';
  if (env.publicEnv === 'dev') {
    targetHost = selfEndpoint;
  }
  if (env.publicEnv === 'prod') {
    targetHost = currentLocaleConfig(targetLocale).url;
  }
  if (env.publicEnv === 'stage') {
    targetHost = currentLocaleConfig(targetLocale).stageUrl;
  }

  return targetHost + pathname;
};

const useLocaleEntry = (props: Props) => {
  const { region } = props;
  const { locale: targetLocale } = region;
  const pathname = usePathname();
  const [redirectHref, setRedirectHref] = useState<string>(
    createRedirectLinkForLocale('', region)
  );

  useEffect(() => {
    if (!pathname) return;

    getOtherLocaleMatchingUrl(pathname, targetLocale).then(
      (otherLocaleMatchingUrl) => {
        if (otherLocaleMatchingUrl) {
          setRedirectHref(createRedirectLinkForLocale(pathname, region));
        }
      }
    );
  }, [pathname, region]);

  return { redirectHref };
};

export default useLocaleEntry;
